import { useState, useEffect } from 'react';
import { adminGet } from '../lib/api';

interface Tier {
  vertical: string; tierId: string; tierName: string; priceUsd: number;
  credits: number; stripePriceId: string | null; attestationEnabled: boolean;
  active: boolean; sortOrder: number; description: string | null;
}

export function PricingTable() {
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [filter, setFilter] = useState<'all' | 'rights' | 'crypto'>('all');

  useEffect(() => {
    (async () => {
      const resp = await adminGet('/api/admin/pricing');
      if (resp.ok && resp.data) setTiers((resp.data as { tiers: Tier[] }).tiers || []);
    })();
  }, []);

  const filtered = tiers.filter(t => filter === 'all' || t.vertical === filter);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-bold text-white">Pricing Table</h1>
        <div className="flex gap-1 bg-dark-700 rounded-lg p-0.5">
          {(['all', 'rights', 'crypto'] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1 text-xs rounded-md capitalize transition-colors ${filter === f ? 'bg-rights-500/20 text-rights-500' : 'text-dark-400'}`}>{f}</button>
          ))}
        </div>
      </div>

      <div className="bg-dark-700 rounded-2xl border border-dark-650/50 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-dark-650/50">
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Vertical</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Tier</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Price</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Credits</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Attestation</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Stripe ID</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-dark-650/30">
            {filtered.map(t => (
              <tr key={`${t.vertical}-${t.tierId}`} className="hover:bg-dark-750/50 transition-colors">
                <td className="px-4 py-3"><span className="px-2 py-0.5 text-xs bg-rights-500/10 text-rights-500 rounded">{t.vertical}</span></td>
                <td className="px-4 py-3 text-sm text-white font-medium">{t.tierName}</td>
                <td className="px-4 py-3 text-sm font-mono text-dark-300">${t.priceUsd}</td>
                <td className="px-4 py-3 text-sm font-mono text-dark-400">{t.credits}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs ${t.attestationEnabled ? 'text-severity-low' : 'text-dark-500'}`}>
                    {t.attestationEnabled ? 'Yes' : 'No'}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs font-mono text-dark-500">{t.stripePriceId || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-xs rounded ${t.active ? 'bg-severity-low/10 text-severity-low' : 'bg-dark-600 text-dark-400'}`}>
                    {t.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-dark-500">No pricing tiers found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
